import { useState, useEffect } from 'react';
import firebase from '../firebaseConfig';

/**
 * Hook personnalisé pour récupérer la photo de profil d'un utilisateur depuis la collection profilePictures
 * @param {string} userId - L'ID de l'utilisateur
 * @returns {Object} - Un objet contenant la photo de profil et des fonctions pour la gérer
 */
const useProfilePicture = (userId) => {
  const [profilePicture, setProfilePicture] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger la photo de profil
  useEffect(() => {
    const loadProfilePicture = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const profilePicRef = firebase.firestore().collection('profilePictures').doc(userId);
        const profilePicDoc = await profilePicRef.get();
        
        if (profilePicDoc.exists) {
          const data = profilePicDoc.data();
          setProfilePicture(data.imageData || '');
        } else {
          setProfilePicture('');
        }
        setError(null);
      } catch (err) {
        console.error('Error loading profile picture:', err);
        setError('Error loading profile picture');
      } finally {
        setLoading(false);
      }
    };

    loadProfilePicture();
  }, [userId]);

  // Fonction pour mettre à jour la photo de profil
  const updateProfilePicture = async (imageData) => {
    if (!userId) return false;

    try {
      if (imageData) {
        // Sauvegarder la nouvelle photo
        const profilePicRef = firebase.firestore().collection('profilePictures').doc(userId);
        await profilePicRef.set({
          imageData,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        });
        setProfilePicture(imageData);
      } else {
        // Supprimer la photo
        const profilePicRef = firebase.firestore().collection('profilePictures').doc(userId);
        await profilePicRef.delete().catch(err => {
          // Ignorer l'erreur si le document n'existe pas
          if (err.code !== 'not-found') throw err;
        });
        setProfilePicture('');
      }
      return true;
    } catch (err) {
      console.error('Error updating profile picture:', err);
      setError('Error updating profile picture');
      return false;
    }
  };

  return {
    profilePicture,
    loading,
    error,
    updateProfilePicture
  };
};

export default useProfilePicture;
